/**
 * Bilplatsavtal · Version 2026 (Formulär R-05) — IR twin of the legacy
 * `bilplats@v1` React template: upplåtelse av mark för bilplats.
 *
 * Legal text verbatim from `base-templates/Bilplatsavtal – kopiera.docx`
 * (Haymanot-godkänd mall). DO NOT paraphrase or reorder: a wording change
 * is a new block version.
 */
import { attachmentList, attachmentRef, em, nodes, option, optionList, p, section, t, val } from '@hyranai/regta-document-ir';
import { contextAttachments, dateVal, fieldRows, numVal, optionChosen, optionVal, partyDetailsBlock, propertySlot, referencedAttachments, selectSlot, shown, signingBlockDefinition, slotText, textVal, ticked, whenOption, } from '../common/index.js';
import { clauseBody } from '../garage/kit.js';
const SOURCE = 'base-templates/Bilplatsavtal – kopiera.docx';
const LEGAL = {
    plane: 'legal',
    status: 'published',
    language: 'sv-SE',
    legalReview: { reviewedBy: 'Haymanot', source: SOURCE },
};
/* ------------------------------------------------------------------ */
/* Option codebooks (ADR-0062) — value = legacy stored value            */
/* ------------------------------------------------------------------ */
export const ROOF_OPTIONS = [
    { value: 'Har tak', label: 'Har tak', code: 'har_tak', summary: 'The parking space is covered (has a roof).' },
    { value: 'Har inte tak', label: 'Har inte tak', code: 'inget_tak', summary: 'The parking space is uncovered (no roof).' },
];
export const SHARED_USE_OPTIONS = [
    { value: 'Alla dagar och tider', label: 'Alla dagar och tider', code: 'alla_tider', summary: 'The space may be used all days and hours.' },
    {
        value: 'Endast nedanstående dagar och tider',
        label: 'Endast nedanstående dagar och tider',
        code: 'angivna_tider',
        summary: 'The space may be used only on the days and hours specified below.',
    },
    { value: 'Enligt bilaga', label: 'Enligt bilaga', code: 'enligt_bilaga', summary: 'Usage times are set out in an attached annex.' },
];
export const ELECTRICITY_OPTIONS = [
    { value: 'Utan elanslutning', label: 'Utan elanslutning', code: 'utan_el', summary: 'The space has no electrical connection.' },
    {
        value: 'Med elanslutning för motorvärmare/kupévärmare',
        label: 'Med elanslutning för motorvärmare/kupévärmare',
        code: 'motorvarmare',
        summary: 'Electrical connection for an engine/cabin heater.',
    },
    {
        value: 'Med elanslutning för annat ändamål',
        label: 'Med elanslutning för annat ändamål',
        code: 'annat_andamal',
        summary: 'Electrical connection for another specified purpose.',
    },
    { value: 'Med laddare för laddbart fordon', label: 'Med laddare för laddbart fordon', code: 'laddare', summary: 'Charger for a rechargeable vehicle.' },
];
export const FEE_INCLUSION_OPTIONS = [
    { value: 'Total avgift', label: 'Total avgift', code: 'total', summary: 'The fee is all-inclusive (total fee).' },
    {
        value: 'Avgift exkluderande följande tillägg',
        label: 'Avgift exkluderande följande tillägg',
        code: 'exkl_tillagg',
        summary: 'The fee excludes the add-ons listed below, billed separately.',
    },
];
export const ELECTRICITY_COST_OPTIONS = [
    { value: 'Ingår i avgiften', label: 'Ingår i avgiften', code: 'ingar', summary: 'Electricity cost is included in the fee.' },
    { value: 'Faktureras efter mätning', label: 'Faktureras efter mätning', code: 'efter_matning', summary: 'Electricity is invoiced based on metering.' },
    {
        value: 'Utgår enligt mätare för bilplatsen',
        label: 'Utgår enligt mätare för bilplatsen',
        code: 'enligt_matare',
        summary: 'Electricity is charged per the meter for the parking space.',
    },
    {
        value: 'Beräknas på bilplatsens del i gemensam strömförbrukning',
        label: 'Beräknas på bilplatsens del i gemensam strömförbrukning',
        code: 'gemensam_del',
        summary: "Electricity is calculated as the space's share of shared consumption.",
    },
    {
        value: 'Beräknas på följande sätt',
        label: 'Beräknas på följande sätt',
        code: 'eget_satt',
        summary: 'Electricity is calculated by a custom method described below.',
    },
];
export const CLEANING_COST_OPTIONS = [
    { value: 'Ingår i avgiften', label: 'Ingår i avgiften', code: 'ingar', summary: 'Cleaning cost is included in the fee.' },
    {
        value: 'Debiteras särskilt',
        label: 'Debiteras särskilt – med utgångspunkt i bilplatsens del i gemensam städkostnad',
        code: 'sarskilt',
        summary: "Cleaning is billed separately based on the space's share of shared cleaning cost.",
    },
];
export const SNOW_COST_OPTIONS = [
    { value: 'Ingår i avgiften', label: 'Ingår i avgiften', code: 'ingar', summary: 'Snow clearing cost is included in the fee.' },
    {
        value: 'Debiteras särskilt',
        label: 'Debiteras särskilt – med utgångspunkt i bilplatsens del i gemensam snöröjningskostnad',
        code: 'sarskilt',
        summary: "Snow clearing is billed separately based on the space's share of shared snow-clearing cost.",
    },
];
export const VAT_OPTIONS = [
    {
        value: 'Mervärdesskattepliktig vid undertecknande',
        label: 'Mervärdesskattepliktig vid undertecknande',
        code: 'momspliktig',
        summary: 'The letting is subject to VAT at the time of signing.',
    },
    {
        value: 'Inte mervärdesskattepliktig vid undertecknande',
        label: 'Inte mervärdesskattepliktig vid undertecknande',
        code: 'ej_momspliktig',
        summary: 'The letting is not subject to VAT at the time of signing.',
    },
];
export const FEE_ADJUSTMENT_OPTIONS = [
    { value: 'Indexreglering (KPI 2020)', label: 'Indexreglering (KPI, basår 2020)', code: 'index_kpi', summary: 'Fee is index-adjusted to the CPI (base year 2020).' },
    { value: 'Fast årlig höjning', label: 'Fast årlig höjning', code: 'fast_hojning', summary: 'Fee increases by a fixed percentage each year.' },
];
export const PAYMENT_PERIOD_OPTIONS = [
    { value: 'Kalenderkvartal', label: 'Kalenderkvartal', code: 'kvartal', summary: 'Fee is paid per calendar quarter.' },
    { value: 'Kalendermånad', label: 'Kalendermånad', code: 'manad', summary: 'Fee is paid per calendar month.' },
];
export const PAYMENT_METHOD_OPTIONS = [
    { value: 'Enligt avisering', label: 'Enligt avisering (rekommenderas)', code: 'avisering', summary: 'Paid per invoice notice (recommended).' },
    { value: 'Plusgiro', label: 'Plusgiro', code: 'plusgiro', summary: 'Paid to a plusgiro account number.' },
    { value: 'Bankgiro', label: 'Bankgiro', code: 'bankgiro', summary: 'Paid to a bankgiro account number.' },
];
const [ROOF_YES, ROOF_NO] = ROOF_OPTIONS;
const [, USE_SPECIFIED, USE_ANNEX] = SHARED_USE_OPTIONS;
const [, EL_HEATER, EL_OTHER, EL_CHARGER] = ELECTRICITY_OPTIONS;
const COST_CUSTOM = ELECTRICITY_COST_OPTIONS[4];
const [VAT_LIABLE, VAT_EXEMPT] = VAT_OPTIONS;
const [ADJ_INDEX, ADJ_FIXED] = FEE_ADJUSTMENT_OPTIONS;
const PERIOD_MONTH = PAYMENT_PERIOD_OPTIONS[1];
const [, PAY_PG, PAY_BG] = PAYMENT_METHOD_OPTIONS;
const hasElectricConnection = (value) => [EL_HEATER, EL_OTHER, EL_CHARGER].some((o) => optionChosen(ELECTRICITY_OPTIONS, value, o));
/** Legacy `attachmentRefLabel()` — "{namn} (Bilaga N)", else a fill-in rule. */
const annexNameAndNumber = (ctx, value, key) => {
    const decl = referencedAttachments(ctx, value)[0];
    return decl
        ? [attachmentRef(decl.id, 'label'), t(' ('), attachmentRef(decl.id, 'number'), t(')')]
        : [val(null, { blankWidth: 'long', sourceKey: key })];
};
/* ------------------------------------------------------------------ */
/* § Upplåtare / § Nyttjanderättshavare                                */
/* ------------------------------------------------------------------ */
export const upplatare = partyDetailsBlock({
    blockKey: 'bilplats.upplatare',
    ref: 'upplatare',
    title: 'Upplåtare',
    summary: 'The grantor: name, id number, address and phone, each pre-filled from the lessor party.',
    role: 'lessor',
    ordinal: 0,
    fields: [
        { key: 'lessor_name', label: 'Namn', field: 'name', required: true },
        { key: 'lessor_org_number', label: 'Personnr / Org.nr', field: 'legalId', required: true },
        { key: 'lessor_address', label: 'Adress', field: 'address', required: true },
        { key: 'lessor_phone', label: 'Telefon', field: 'phone' },
    ],
});
export const nyttjanderattshavare = partyDetailsBlock({
    blockKey: 'bilplats.nyttjanderattshavare',
    ref: 'nyttjanderattshavare',
    title: 'Nyttjanderättshavare',
    summary: 'The holder of the right of use: name, id number, address and phone, each pre-filled from the lessee party.',
    role: 'lessee',
    ordinal: 0,
    fields: [
        { key: 'user_name', label: 'Namn', field: 'name', required: true },
        { key: 'user_org_number', label: 'Personnr / Org.nr', field: 'legalId', required: true },
        { key: 'user_address', label: 'Adress', field: 'address', required: true },
        { key: 'user_phone', label: 'Telefon', field: 'phone' },
    ],
});
/* ------------------------------------------------------------------ */
/* § Bilplatsens läge, nummer och typ                                  */
/* ------------------------------------------------------------------ */
export const lage = {
    ...LEGAL,
    blockKey: 'bilplats.lage',
    version: 'v1',
    title: 'Bilplatsens läge, nummer och typ',
    summary: 'Where the space is (municipality, property, address), its number or "no specific spot", whether it has a roof, and its area.',
    slots: [
        propertySlot('property_municipality', 'Kommun', 'municipality', { required: true }),
        propertySlot('property_designation', 'Fastighetsbeteckning', 'designation', { required: true }),
        propertySlot('bilplats_address', 'Bilplatsens adress', 'address', { type: 'textarea', required: true }),
        { key: 'bilplats_number', label: 'Bilplatsnummer', type: 'text', visibleWhen: { key: 'bilplats_no_specific_spot', equals: false } },
        { key: 'bilplats_no_specific_spot', label: 'Ingen särskild angiven plats', type: 'boolean', default: false },
        selectSlot('roof_status', 'Tak', ROOF_OPTIONS, { required: true }),
        { key: 'area_sqm', label: 'Yta (m²)', type: 'number', min: 0 },
    ],
    compile: (input, ctx) => [
        section('bilplats-lage', 'Bilplatsens läge, nummer och typ', nodes(p([
            t('Bilplatsen är belägen i '),
            textVal(input, 'property_municipality'),
            t(' kommun, i fastigheten '),
            textVal(input, 'property_designation'),
            t('. Bilplatsen har adressen '),
            textVal(input, 'bilplats_address'),
            t('.'),
        ]), fieldRows([
            [
                'Bilplatsnummer',
                input.bilplats_no_specific_spot === true ? [t('Ingen särskild angiven plats')] : [textVal(input, 'bilplats_number', 'short')],
            ],
            [
                'Tak',
                [
                    optionList([
                        option(ticked(ctx, optionChosen(ROOF_OPTIONS, input.roof_status, ROOF_YES)), 'har tak', { code: ROOF_YES.code }),
                        option(ticked(ctx, optionChosen(ROOF_OPTIONS, input.roof_status, ROOF_NO)), 'har inte tak', { code: ROOF_NO.code }),
                    ], { sourceKey: 'roof_status', select: 'single', layout: 'inline' }),
                ],
            ],
            ['Yta (m²)', [numVal(ctx, input, 'area_sqm')]],
        ]))),
    ],
};
/* ------------------------------------------------------------------ */
/* § Användning                                                        */
/* ------------------------------------------------------------------ */
export const anvandning = {
    ...LEGAL,
    blockKey: 'bilplats.anvandning',
    version: 'v1',
    title: 'Användning',
    summary: 'The space is let as-is for parking the stated number of cars, of which some are electric with charging.',
    slots: [
        { key: 'car_count', label: 'Antal personbilar', type: 'number', required: true, min: 0, integer: true, default: 1 },
        { key: 'ev_count', label: 'Antal eldrivna personbilar med laddning', type: 'number', min: 0, integer: true, default: 0 },
    ],
    compile: (input, ctx) => [
        section('anvandning', 'Användning', nodes(p('Bilplatsen upplåts, om inget annat anges, i befintligt skick att användas till uppställningsplats för av nyttjanderättshavaren nyttjande:'), fieldRows([
            ['Personbilar', [numVal(ctx, input, 'car_count'), t(' st.')]],
            ['Eldrivna personbilar med laddning', [numVal(ctx, input, 'ev_count'), t(' st.')]],
        ]))),
    ],
};
/* ------------------------------------------------------------------ */
/* § Samnyttjande                                                      */
/* ------------------------------------------------------------------ */
const periodLine = (lead, input, days, from, to) => [
    t(lead),
    textVal(input, days),
    t(' mellan kl '),
    textVal(input, from, 'short'),
    t(' och kl '),
    textVal(input, to, 'short'),
    t('.'),
];
export const samnyttjande = {
    ...LEGAL,
    blockKey: 'bilplats.samnyttjande',
    version: 'v1',
    title: 'Samnyttjande',
    summary: 'When the space may be used: always, only on up to two stated day/time periods, or per an annex.',
    slots: [
        selectSlot('shared_use', 'Bilplatsen får nyttjas', SHARED_USE_OPTIONS, { required: true, default: SHARED_USE_OPTIONS[0].code }),
        {
            key: 'shared_use_annex',
            label: 'Bilaga – nyttjandetid',
            type: 'attachment',
            required: true,
            visibleWhen: whenOption('shared_use', USE_ANNEX),
            help: 'Välj eller ladda upp bilagan som anger när bilplatsen får nyttjas.',
        },
        { key: 'shared_days_1', label: 'Veckodagar (period 1)', type: 'text', visibleWhen: whenOption('shared_use', USE_SPECIFIED), help: 'T.ex. "måndag–fredag"' },
        { key: 'shared_time_1_from', label: 'Från kl (period 1)', type: 'text', visibleWhen: whenOption('shared_use', USE_SPECIFIED), help: 'T.ex. "08:00"' },
        { key: 'shared_time_1_to', label: 'Till kl (period 1)', type: 'text', visibleWhen: whenOption('shared_use', USE_SPECIFIED), help: 'T.ex. "17:00"' },
        {
            key: 'shared_days_2',
            label: 'Veckodagar (period 2)',
            type: 'text',
            visibleWhen: whenOption('shared_use', USE_SPECIFIED),
            help: 'Valfri – om en andra tidsperiod gäller',
        },
        { key: 'shared_time_2_from', label: 'Från kl (period 2)', type: 'text', visibleWhen: whenOption('shared_use', USE_SPECIFIED) },
        { key: 'shared_time_2_to', label: 'Till kl (period 2)', type: 'text', visibleWhen: whenOption('shared_use', USE_SPECIFIED) },
    ],
    compile: (input, ctx) => {
        const isAnnex = optionChosen(SHARED_USE_OPTIONS, input.shared_use, USE_ANNEX);
        const isSpecified = optionChosen(SHARED_USE_OPTIONS, input.shared_use, USE_SPECIFIED);
        const annexes = referencedAttachments(ctx, input.shared_use_annex);
        return {
            nodes: [
                section('samnyttjande', 'Samnyttjande', nodes(fieldRows([['Nyttjandetid', [optionVal(SHARED_USE_OPTIONS, input, 'shared_use')]]]), shown(ctx, isAnnex) &&
                    p([t('Bilplatsen får nyttjas enligt vad som anges i '), ...annexNameAndNumber(ctx, input.shared_use_annex, 'shared_use_annex'), t('.')]), ...(shown(ctx, isSpecified)
                    ? [
                        p(periodLine('Veckodagarna ', input, 'shared_days_1', 'shared_time_1_from', 'shared_time_1_to')),
                        shown(ctx, slotText(input.shared_days_2) != null)
                            ? p(periodLine('Samt veckodagarna ', input, 'shared_days_2', 'shared_time_2_from', 'shared_time_2_to'))
                            : null,
                        p([
                            em('Övriga tider disponeras bilplatsen med tillhörande utrustning av upplåtaren eller annan nyttjanderättshavare.'),
                        ]),
                    ]
                    : []))),
            ],
            attachments: isAnnex ? annexes : [],
        };
    },
};
/* ------------------------------------------------------------------ */
/* § Elström                                                           */
/* ------------------------------------------------------------------ */
export const elstrom = {
    ...LEGAL,
    blockKey: 'bilplats.elstrom',
    version: 'v1',
    title: 'Elström',
    summary: 'Whether the space has an electrical connection, and for what: engine heater, another purpose (max watt) or a charger.',
    slots: [
        selectSlot('electricity', 'Elanslutning', ELECTRICITY_OPTIONS, { required: true }),
        { key: 'electricity_other_use', label: 'Annat ändamål', type: 'text', visibleWhen: whenOption('electricity', EL_OTHER), help: 'Beskriv ändamålet' },
        {
            key: 'electricity_max_watt',
            label: 'Högsta sammanlagda effekt (watt)',
            type: 'number',
            min: 0,
            integer: true,
            visibleWhen: whenOption('electricity', EL_OTHER),
        },
        { key: 'charger_type', label: 'Anslutningstyp för laddare', type: 'text', visibleWhen: whenOption('electricity', EL_CHARGER), help: 't.ex. motorvärmare' },
    ],
    compile: (input, ctx) => [
        section('elstrom', 'Elström', nodes(p([em('Bilplatsen upplåts:')]), fieldRows([['Anslutning', [optionVal(ELECTRICITY_OPTIONS, input, 'electricity')]]]), shown(ctx, optionChosen(ELECTRICITY_OPTIONS, input.electricity, EL_OTHER)) &&
            fieldRows([
                ['Annat ändamål', [textVal(input, 'electricity_other_use')]],
                ['Högsta sammanlagda effekt (watt)', [numVal(ctx, input, 'electricity_max_watt')]],
            ]), shown(ctx, optionChosen(ELECTRICITY_OPTIONS, input.electricity, EL_CHARGER)) && fieldRows([['Anslutningstyp', [textVal(input, 'charger_type')]]]))),
    ],
};
/* ------------------------------------------------------------------ */
/* § Upplåtelsetid / § Uppsägningstid / Förlängningstid                */
/* ------------------------------------------------------------------ */
export const upplatelsetid = {
    ...LEGAL,
    blockKey: 'bilplats.upplatelsetid',
    version: 'v1',
    title: 'Upplåtelsetid',
    summary: 'The term of the grant: start and end date.',
    slots: [
        { key: 'start_date', label: 'Från och med', type: 'date', required: true },
        { key: 'end_date', label: 'Till och med', type: 'date', required: true },
    ],
    compile: (input, ctx) => [
        section('upplatelsetid', 'Upplåtelsetid', nodes(fieldRows([['Från och med', [dateVal(ctx, input, 'start_date')]], ['Till och med', [dateVal(ctx, input, 'end_date')]]], { columns: 2 }))),
    ],
};
export const uppsagning = {
    ...LEGAL,
    blockKey: 'bilplats.uppsagning',
    version: 'v1',
    title: 'Uppsägningstid / Förlängningstid',
    summary: 'Written notice period before the term ends; otherwise the agreement renews for the stated extension.',
    slots: [
        { key: 'notice_months', label: 'Uppsägningstid (månader)', type: 'number', required: true, min: 0, integer: true, help: 'Skriftlig uppsägning före upplåtelsetidens utgång.' },
        {
            key: 'extension_months',
            label: 'Förlängningstid (månader)',
            type: 'number',
            required: true,
            min: 0,
            integer: true,
            help: 'Avtalet förlängs automatiskt om uppsägning ej sker.',
        },
    ],
    compile: (input, ctx) => [
        section('uppsagning', 'Uppsägningstid / Förlängningstid', [
            p([
                t('Uppsägning av detta kontrakt ska ske skriftligen minst '),
                numVal(ctx, input, 'notice_months'),
                t(' månad(er) före den avtalade upplåtelsetidens utgång. I annat fall är kontraktet för varje gång förlängt med '),
                numVal(ctx, input, 'extension_months'),
                t(' månad(er).'),
            ]),
        ]),
    ],
};
/* ------------------------------------------------------------------ */
/* § Avgift (with its auto-numbered sub-clauses)                       */
/* ------------------------------------------------------------------ */
/** An unnumbered sub-heading inside a section (the legacy bold run-in heading). */
const subHeading = (ref, title, content) => section(ref, title, content, { numbering: 'none', variant: 'clause', keepTogether: true });
/**
 * A numbered sub-clause of § Avgift, numbered under Avgift's own § (9.1–9.3
 * today) so it always matches its parent. The legacy form's hard-coded
 * "7.1–7.3" is a source defect, deliberately not reproduced.
 */
const subClause = (ref, title, content) => section(ref, title, content, { keepTogether: true });
export const avgift = {
    ...LEGAL,
    blockKey: 'bilplats.avgift',
    version: 'v1',
    title: 'Avgift',
    summary: 'Annual fee and whether it is total or excludes add-ons; how electricity, cleaning and snow clearing are charged.',
    slots: [
        { key: 'annual_fee', label: 'Avgift (kr/år)', type: 'currency', required: true, min: 0 },
        selectSlot('fee_inclusion', 'Avgiftsstruktur', FEE_INCLUSION_OPTIONS, { required: true, default: FEE_INCLUSION_OPTIONS[0].code }),
        selectSlot('electricity_cost', 'Kostnad för elström', ELECTRICITY_COST_OPTIONS),
        {
            key: 'electricity_cost_basis',
            label: 'Beräkningsgrund (elström)',
            type: 'textarea',
            visibleWhen: whenOption('electricity_cost', COST_CUSTOM),
        },
        selectSlot('cleaning_cost', 'Städning', CLEANING_COST_OPTIONS),
        selectSlot('snow_cost', 'Snöröjning', SNOW_COST_OPTIONS),
    ],
    compile: (input, ctx) => [
        section('avgift', 'Avgift', [
            p([
                t('Kronor '),
                numVal(ctx, input, 'annual_fee'),
                t(' per år utgörande '),
                optionVal(FEE_INCLUSION_OPTIONS, input, 'fee_inclusion'),
                t('.'),
            ]),
            subClause('avgift-elstrom', 'Kostnad för elström', nodes(fieldRows([['Hantering', [optionVal(ELECTRICITY_COST_OPTIONS, input, 'electricity_cost')]]]), shown(ctx, optionChosen(ELECTRICITY_COST_OPTIONS, input.electricity_cost, COST_CUSTOM)) &&
                fieldRows([['Beräkningsgrund', [textVal(input, 'electricity_cost_basis')]]]))),
            subClause('avgift-stadning', 'Städning', nodes(fieldRows([['Hantering', [optionVal(CLEANING_COST_OPTIONS, input, 'cleaning_cost')]]]))),
            subClause('avgift-snorojning', 'Snöröjning', nodes(fieldRows([['Hantering', [optionVal(SNOW_COST_OPTIONS, input, 'snow_cost')]]]))),
        ]),
    ],
};
/* ------------------------------------------------------------------ */
/* § Mervärdesskatt (moms)                                             */
/* ------------------------------------------------------------------ */
export const moms = {
    ...LEGAL,
    blockKey: 'bilplats.moms',
    version: 'v1',
    title: 'Mervärdesskatt (moms)',
    summary: 'Whether the letting is VAT-liable at signing; VAT is then (or if it becomes liable) paid on top of the fee.',
    slots: [selectSlot('vat_status', 'Mervärdesskatt', VAT_OPTIONS, { required: true })],
    compile: (input, ctx) => [
        section('moms', 'Mervärdesskatt (moms)', nodes(shown(ctx, optionChosen(VAT_OPTIONS, input.vat_status, VAT_LIABLE)) &&
            p('Vid avtalets undertecknande är upplåtelsen mervärdesskattepliktig enligt mervärdesskattelagen (2023:200). Nyttjanderättshavaren ska utöver avgiften betala vid varje tillfälle gällande moms.'), shown(ctx, optionChosen(VAT_OPTIONS, input.vat_status, VAT_EXEMPT)) &&
            p('Vid avtalets undertecknande är upplåtelsen inte mervärdesskattepliktig. Om upplåtelsen blir mervärdesskattepliktig ska nyttjanderättshavaren utöver avgiften betala vid varje tillfälle gällande moms.'), p('Momsen, som ska betalas samtidigt med avgiften, beräknas på avgiften inklusive eventuella tillägg eller andra ersättningar enligt vid varje tillfälle gällande skattesats.'))),
    ],
};
/* ------------------------------------------------------------------ */
/* § Ändring av avgift                                                 */
/* ------------------------------------------------------------------ */
export const andringAvgift = {
    ...LEGAL,
    blockKey: 'bilplats.andring-avgift',
    version: 'v1',
    title: 'Ändring av avgift',
    summary: 'How the fee changes: CPI indexation (base year 2020) on a stated base fee, or a fixed yearly percentage increase.',
    slots: [
        selectSlot('fee_adjustment_model', 'Justeringsmodell', FEE_ADJUSTMENT_OPTIONS, { required: true }),
        { key: 'base_fee_amount', label: 'Basavgift (kr)', type: 'currency', min: 0, visibleWhen: whenOption('fee_adjustment_model', ADJ_INDEX) },
        {
            key: 'fee_index_alt_base_year',
            label: 'Avvikande bastal (år)',
            type: 'text',
            visibleWhen: whenOption('fee_adjustment_model', ADJ_INDEX),
            help: 'Lämna tomt för standardbastal.',
        },
        {
            key: 'fixed_increase_start_date',
            label: 'Första höjning från och med',
            type: 'date',
            visibleWhen: whenOption('fee_adjustment_model', ADJ_FIXED),
        },
        {
            key: 'fixed_increase_percent',
            label: 'Höjning per år (%)',
            type: 'number',
            min: 0,
            max: 100,
            visibleWhen: whenOption('fee_adjustment_model', ADJ_FIXED),
        },
    ],
    compile: (input, ctx) => {
        const altBaseYear = slotText(input.fee_index_alt_base_year);
        // The inline "avvikande bastal" sentence follows the raw value, not the
        // blank-form rule: a blank form never prints it (as in the source).
        const printAltSentence = ctx.mode !== 'blank' && altBaseYear != null;
        return [
            section('andring-avgift', 'Ändring av avgift', nodes(fieldRows([['Modell', [optionVal(FEE_ADJUSTMENT_OPTIONS, input, 'fee_adjustment_model')]]]), ...(shown(ctx, optionChosen(FEE_ADJUSTMENT_OPTIONS, input.fee_adjustment_model, ADJ_INDEX))
                ? [
                    shown(ctx, altBaseYear != null) ? fieldRows([['Avvikande bastal', [textVal(input, 'fee_index_alt_base_year', 'short')]]]) : null,
                    p([
                        t('Avgiften ändras under löpande avtal i enlighet med följande indexklausul. Under nyttjanderättsförhållandet ska avgiften uppgå till '),
                        numVal(ctx, input, 'base_fee_amount'),
                        t(' kr och utgöra basavgift.'),
                    ]),
                    p([
                        t('Under nyttjanderättstiden ska tillägg till basavgiften utgå med hänsyn till förändringar i konsumentprisindex (totalindex med 2020 som basår). För avtal som börjar löpa under perioden 1 januari–30 juni anses basavgiften anpassad till indextalet för oktober månad föregående år. För avtal som börjar löpa under perioden 1 juli–31 december anses basavgiften i stället anpassad till indextalet för oktober månad samma år. Det indextalet utgör bastalet, om inte parterna skriftligen avtalat om avvikande bastal.'),
                        ...(printAltSentence
                            ? [
                                t(' Har avvikande bastal avtalats, utgörs bastalet av indextalet för oktober månad år '),
                                val(altBaseYear, { sourceKey: 'fee_index_alt_base_year' }),
                                t('.'),
                            ]
                            : []),
                    ]),
                    p('Skulle indextalet för en påföljande oktobermånad ha stigit eller sjunkit i förhållande till bastalet, ska tillägget motsvara den procentuella förändringen mellan bastalet och det aktuella indextalet för respektive oktobermånad. Tillägget kan såväl öka som minska mellan olika år. Avgiften ska dock aldrig understiga det i avtalet angivna basavgiftsbeloppet. Avgiftsändringen träder i kraft från och med den 1 januari närmast efter det att oktobertalet föranlett omräkning. Det justerade tillägget avrundas till närmaste hela krontal.'),
                    p('Om bastalet utgörs av ett indextal fastställt med basår 1980 ska det omräknas till index med basår 2020 enligt följande formel: bastalet divideras med det officiellt fastställda medeltalet av KPI för år 2020 uttryckt med basår 1980 – för närvarande 335,92 – och kvoten multipliceras med 100. Resultatet avrundas till två decimaler. Har SCB fastställt ett reviderat medeltal för 2020 vid tidpunkten för omräkning, ska det reviderade talet användas.'),
                ]
                : []), shown(ctx, optionChosen(FEE_ADJUSTMENT_OPTIONS, input.fee_adjustment_model, ADJ_FIXED)) &&
                p([
                    t('Den 1 januari varje år, från och med '),
                    dateVal(ctx, input, 'fixed_increase_start_date'),
                    t(', ska den vid tidpunkten gällande avgiften höjas med '),
                    numVal(ctx, input, 'fixed_increase_percent'),
                    t(' procent.'),
                ]))),
        ];
    },
};
/* ------------------------------------------------------------------ */
/* § Avgiftens betalning                                               */
/* ------------------------------------------------------------------ */
export const betalning = {
    ...LEGAL,
    blockKey: 'bilplats.betalning',
    version: 'v1',
    title: 'Avgiftens betalning',
    summary: 'The fee is paid in advance before each calendar quarter or month, per invoice or to a plusgiro/bankgiro account; late payment carries interest.',
    slots: [
        selectSlot('payment_period', 'Betalningsperiod', PAYMENT_PERIOD_OPTIONS, { required: true, default: PAYMENT_PERIOD_OPTIONS[0].code }),
        selectSlot('payment_method', 'Betalningssätt', PAYMENT_METHOD_OPTIONS, { required: true, default: PAYMENT_METHOD_OPTIONS[0].code }),
        {
            key: 'payment_account',
            label: 'Konto (PG/BG-nummer)',
            type: 'text',
            visibleWhen: whenOption('payment_method', PAY_PG, PAY_BG),
            help: 'T.ex. 123-4567',
        },
    ],
    compile: (input, ctx) => {
        const isMonth = optionChosen(PAYMENT_PERIOD_OPTIONS, input.payment_period, PERIOD_MONTH);
        const isPg = optionChosen(PAYMENT_METHOD_OPTIONS, input.payment_method, PAY_PG);
        const isGiro = isPg || optionChosen(PAYMENT_METHOD_OPTIONS, input.payment_method, PAY_BG);
        return [
            section('betalning', 'Avgiftens betalning', nodes(p([
                t('Avgiften tillsammans med förekommande tillägg betalas i förskott senast sista vardagen före början på varje '),
                ctx.mode === 'blank'
                    ? t('kalenderkvartal')
                    : val(isMonth ? 'kalendermånad' : 'kalenderkvartal', { sourceKey: 'payment_period', blankWidth: 'medium' }),
                t('.'),
            ]), fieldRows([['Betalningssätt', [optionVal(PAYMENT_METHOD_OPTIONS, input, 'payment_method')]]]), shown(ctx, isGiro) && fieldRows([[isPg ? 'Plusgiro nr' : 'Bankgiro nr', [textVal(input, 'payment_account')]]]), p('Vid försenad betalning ska nyttjanderättshavaren betala dröjsmålsränta och ersättning för betalningspåminnelse, inkasso och dylikt enligt lag.'))),
        ];
    },
};
/* ------------------------------------------------------------------ */
/* § Avtalsvillkor                                                     */
/* ------------------------------------------------------------------ */
const AVTALSVILLKOR_TEXT = 'Nyttjanderättshavaren får inte utöver befintliga skyddsanordningar sätta upp ytterligare sådana eller andra anordningar av permanent eller tillfällig karaktär.\n\n' +
    'Önskar nyttjanderättshavaren sätta upp skylt med bilnummer ska skylten och uppsättningsanordningen godkännas av upplåtaren.\n\n' +
    'Nyttjanderättshavaren förbinder sig att på bilplatsen inte utan upplåtarens samtycke låta uppställa fordonsvrak.\n\n' +
    'Underhåll av bilplatsen och därtill hörande anordningar med undantag av ovan nämnda skylt bekostas och ombesörjs av upplåtaren.\n\n' +
    'Nyttjanderättshavaren får inte låta utföra biltvätt på bilplatsen.\n\n' +
    'Upplåtaren har rätt till att få tillträde till bilplatsen för att utföra nödvändiga reparations- och underhållsarbeten.\n\n' +
    'För infart till och utfart från bilplatsen ska nyttjanderättshavaren använda mark som har iordningställts för ändamålet.\n\n' +
    'Om inte annat har angetts under särskilda bestämmelser svarar upplåtaren för sedvanlig renhållning, snöröjning och sandning på infartsväg till bilplatsen medan nyttjanderättshavaren svarar för sådana åtgärder när det gäller själva bilplatsen.\n\n' +
    'Upplåtaren friskriver sig från ansvar för all skada på nyttjanderättshavarens fordon och på egendom som förvaras i fordonet, såvida skadan inte har uppkommit genom upplåtarens eget vållande. Upplåtaren friskriver sig också från ansvar för intrång från obehöriga.\n\n' +
    'För tillfälligt hinder eller men i nyttjanderätten till följd av underhållsarbeten på bilplatsen eller andra därmed jämförbara förhållanden har nyttjanderättshavaren inte rätt till nedsättning av avgiften.';
const EL_SARSKILT_TEXT = 'För upplåtelse av bilplats med eluttag eller laddare gäller särskilt:\n\n' +
    '• att för ändamålet godkänd adapter och anslutningskabel utöver fast kabel tillhandahålls av nyttjanderättshavaren,\n' +
    '• att endast elapparat, adapter och anslutningskabel som uppfyller gällande säkerhetskrav får användas,\n' +
    '• att eluttaget inte får användas för laddning av el-/hybridfordon om det inte särskilt godkänts av upplåtaren,\n' +
    '• att eluttaget alltid ska hållas låst,\n' +
    '• att anslutningskabel inte får vara ansluten till eluttaget då kabeln inte är kopplad till elapparat,\n' +
    '• att nyttjanderättshavaren omedelbart till upplåtaren ska anmäla uppkomna fel på eluttaget eller laddaren,\n' +
    '• att nyttjanderättshavaren ansvarar för att elapparat, anslutet fordon, anslutningskabel och adapter vid användningen är i fullgott skick,\n' +
    '• att endast det eluttag eller laddpunkt som tillhör bilplatsen får användas,\n' +
    '• att säkerhetsföreskrifter och bruksanvisning som framgår av anslag vid laddaren eller som tillställts nyttjanderättshavaren på annat sätt iakttas vid användning av laddaren.';
const OVERLATELSE_TEXT = 'Nyttjanderätten till bilplatsen enligt detta avtal får inte utan upplåtarens medgivande överlåtas eller inskrivas.\n\n' +
    'Nyttjanderättshavarens rätt enligt detta avtal är förverkad och upplåtaren berättigad att säga upp avtalet till omedelbart upphörande om nyttjanderättshavaren inte i rätt tid betalar angiven avgift tillsammans med förekommande tillägg och moms eller i övrigt bryter mot bestämmelserna i 8 kap. 23 § jordabalken.\n\n' +
    'För nyttjanderätten av bilplatsen gäller särskilda ordningsföreskrifter.';
export const avtalsvillkor = {
    ...LEGAL,
    blockKey: 'bilplats.avtalsvillkor',
    version: 'v1',
    title: 'Avtalsvillkor',
    summary: 'Standard terms of use, the extra rules for spaces with an outlet or charger, and assignment/forfeiture under 8 kap. 23 § jordabalken.',
    slots: [selectSlot('electricity', 'Elanslutning', ELECTRICITY_OPTIONS)],
    compile: (input, ctx) => [
        section('avtalsvillkor', 'Avtalsvillkor', nodes(...clauseBody(AVTALSVILLKOR_TEXT), shown(ctx, hasElectricConnection(input.electricity)) &&
            subHeading('el-sarskilt', 'Särskilt för bilplats med eluttag eller laddare', clauseBody(EL_SARSKILT_TEXT)), subHeading('overlatelse', 'Överlåtelse, förverkande och ordningsföreskrifter', clauseBody(OVERLATELSE_TEXT)))),
    ],
};
/* ------------------------------------------------------------------ */
/* § Bilagor / § Underskrifter                                         */
/* ------------------------------------------------------------------ */
/**
 * Lists every bilaga the contract carries (legacy `AttachmentList`): the
 * host's attachments are declared here so the assembler numbers and lists
 * them, rather than a hand-typed list that could drift.
 */
export const bilagor = {
    ...LEGAL,
    blockKey: 'bilplats.bilagor',
    version: 'v1',
    title: 'Bilagor',
    summary: 'Auto-generated list of the contract’s attachments.',
    slots: [],
    compile: (_input, ctx) => {
        const declared = ctx.mode === 'blank' ? [] : contextAttachments(ctx);
        return {
            nodes: [section('bilagor', 'Bilagor', [declared.length ? attachmentList() : p([em('Inga bilagor bifogade.')])])],
            attachments: declared,
        };
    },
};
export const underskrift = signingBlockDefinition({
    blockKey: 'bilplats.underskrift',
    summary: 'Signature panels for both parties, with the digital or physical-originals statement above them.',
});
export const BILPLATS_BLOCKS = [
    upplatare,
    nyttjanderattshavare,
    lage,
    anvandning,
    samnyttjande,
    elstrom,
    upplatelsetid,
    uppsagning,
    avgift,
    moms,
    andringAvgift,
    betalning,
    avtalsvillkor,
    bilagor,
    underskrift,
];
//# sourceMappingURL=blocks.js.map